import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import ButtonModifierBlock from "@v2/features/modifiers/ButtonModifierBlock";
import ContainerModifierBlock from "@v2/features/modifiers/ContainerModifierBlock";
import { useModifiers } from "@v2/features/modifiers/hooks/useModifiers";
import IconModifierBlock from "@v2/features/modifiers/IconModifierBlock";
import ImageModifierBlock from "@v2/features/modifiers/ImageModifierBlock";
import {
  useDefaultSelectedElementDesignation,
  useIsDeleting,
  useModifiersStoreActions,
} from "@v2/features/modifiers/store/ModifiersStoreContext";
import TextModifierBlock from "@v2/features/modifiers/TextModifierBlock";
import { ChevronDown, Trash2, X } from "lucide-react";
import { Resizable } from "react-resizable";
import useMeasure from "react-use-measure";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

import LinkModifierBlock from "./LinkModifierBlock";
import VideoModifierBlock from "./VideoModifierBlock";

function UnknownModifierBlock() {
  return (
    <div className="text-xs text-muted">
      This element cannot be modified directly. Please use the chat to change
      this element.
    </div>
  );
}

type ModifierComponentProps = {
  onModifiersValueChange: (
    modifiers: UniversalModifierValue[],
    stableElementId: string,
  ) => void;
};

type ModifierComponentType = React.ComponentType<ModifierComponentProps>;

const ModifiersScrollButton = () => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  if (isAtBottom) {
    return null;
  }

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-in fade-in duration-200">
      <Button
        icon
        variant="secondary"
        onClick={() => void scrollToBottom()}
        aria-label="Scroll to bottom"
        UNSAFE_className="group w-6 h-6 min-w-6 min-h-6 rounded-full p-0 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
      >
        <ChevronDown className="w-3 h-3 text-muted-foreground transition-colors duration-200 group-hover:text-foreground" />
      </Button>
    </div>
  );
};

const MODIFIER_BLOCKS = {
  text: {
    displayName: "Text",
    component: TextModifierBlock,
  },
  container: {
    displayName: "Container",
    component: ContainerModifierBlock,
  },
  button: {
    displayName: "Button",
    component: ButtonModifierBlock,
  },
  image: {
    displayName: "Image",
    component: ImageModifierBlock,
  },
  link: {
    displayName: "Link",
    component: LinkModifierBlock,
  },
  video: {
    displayName: "Video",
    component: VideoModifierBlock,
  },
  icon: {
    displayName: "Icon",
    component: IconModifierBlock,
  },
  "product-section": {
    displayName: "Product Section",
    component: ContainerModifierBlock,
  },
  unknown: {
    displayName: "Unknown",
    component: UnknownModifierBlock,
  },
} as const;

export default function Modifiers({
  maxHeight,
}: {
  maxHeight?: number | null;
}) {
  const modifierActions = useModifiersStoreActions();
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const { applyModifier, isProcessing } = useModifiers();
  const isDeleting = useIsDeleting();

  // NOTE (Nick, 2025-12-12): Destructure specific actions for stable references in effect dependencies
  const { setSelectedElementDesignations, setIsDeleting } = modifierActions;

  const wasProcessingRef = useRef(false);
  const [headerSectionRef, { height: headerHeight }] = useMeasure();
  const [height, setHeight] = useState<number | null>(null);
  const resolvedMaxHeight = maxHeight ?? 0;
  const resolvedMinHeight = headerHeight ?? 0;

  // NOTE (Nick, 2026-02-05): Stable height for modifier panel when winddow is resized
  useEffect(() => {
    if (height === null) {
      return;
    }

    const clampedHeight = Math.min(
      resolvedMaxHeight,
      Math.max(resolvedMinHeight, height),
    );

    if (clampedHeight !== height) {
      setHeight(clampedHeight);
    }
  }, [height, resolvedMaxHeight, resolvedMinHeight]);

  // NOTE (Nick, 2025-12-11): Clear selection when delete completes.
  // We track when isProcessing transitions from true → false while isDeleting is true.
  // This ensures we wait for the actual delete to complete, not just fire immediately.
  useEffect(() => {
    if (isProcessing) {
      wasProcessingRef.current = true;
    } else if (wasProcessingRef.current && isDeleting) {
      // isProcessing went from true to false while we were deleting
      setSelectedElementDesignations(null);
      setIsDeleting(false);
      wasProcessingRef.current = false;
    }
  }, [isDeleting, isProcessing, setSelectedElementDesignations, setIsDeleting]);

  const handleDelete = useCallback(() => {
    if (!defaultDesignation || isDeleting) {
      return;
    }

    modifierActions.setIsDeleting(true);

    applyModifier({
      modifier: {
        type: "delete",
        value: null,
      },
      stableElementId: defaultDesignation.element.stableElementId,
    });
  }, [defaultDesignation, isDeleting, applyModifier, modifierActions]);

  const handleModifiersChange = useCallback(
    (modifiers: UniversalModifierValue[], stableElementId: string) => {
      for (const modifier of modifiers) {
        applyModifier({ modifier, stableElementId });
      }
    },
    [applyModifier],
  );

  if (!defaultDesignation) {
    return null;
  }

  const elementType = defaultDesignation.element.type;

  const block = MODIFIER_BLOCKS[elementType];
  const ModifierComponent = block.component as ModifierComponentType;

  const resolvedHeight = height ?? resolvedMaxHeight * 0.75;

  return (
    <Resizable
      axis="y"
      height={resolvedHeight}
      width={0}
      minConstraints={[0, resolvedMinHeight]}
      maxConstraints={[0, resolvedMaxHeight]}
      resizeHandles={["n"]}
      handle={
        <div className="absolute top-0 left-0 right-0 h-2 pt-2 cursor-ns-resize flex items-center justify-center">
          <div className="w-8 h-1 bg-border rounded-full" />
        </div>
      }
      onResize={(_event, { size }) => {
        setHeight(size.height);
      }}
    >
      <div
        className={twMerge(
          "relative flex min-h-0 max-h-full overflow-hidden w-full flex-col border-border rounded-xl bg-white border-0.5 shadow-sm",
          isDeleting && "pointer-events-none",
        )}
        style={{ height: resolvedHeight }}
      >
        {isDeleting && (
          <div className="absolute inset-0 bg-white/60 rounded-xl z-10" />
        )}
        <div
          ref={headerSectionRef}
          className="flex items-center justify-between flex-shrink-0 px-4 pt-2 pb-2"
        >
          <div className="flex items-center header-h6">
            {defaultDesignation
              ? MODIFIER_BLOCKS[defaultDesignation.element.type].displayName
              : "Selected element"}
          </div>
          <div className="flex items-center gap-1">
            <Button
              icon
              variant="tertiary"
              size="base"
              onClick={handleDelete}
              aria-label="Delete element"
            >
              <Trash2 />
            </Button>
            <Button
              icon
              variant="tertiary"
              size="base"
              onClick={() => {
                modifierActions.setSelectedElementDesignations(null);
              }}
            >
              <X />
            </Button>
          </div>
        </div>
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="border-t-0.5 border-border flex-shrink-0 pb-3" />
          <StickToBottom
            className="relative flex-1 h-full max-h-full min-h-0 no-scrollbar grid"
            initial="instant"
            resize="instant"
          >
            <StickToBottom.Content className="flex min-h-0 flex-1 flex-col gap-4 px-4 pb-3">
              <ModifierComponent
                onModifiersValueChange={handleModifiersChange}
              />
            </StickToBottom.Content>
            <ModifiersScrollButton />
          </StickToBottom>
        </div>
      </div>
    </Resizable>
  );
}
